'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import styles from './styles.module.css';

interface Toast {
    id: number;
    message: string;
    type: 'info' | 'success' | 'error' | 'warning';
    duration: number;
    isExiting?: boolean;
}

interface ToastContextValue {
    addToast: (message: string, type?: 'info' | 'success' | 'error' | 'warning', duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = (): ToastContextValue => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

const EXIT_ANIMATION_DURATION = 300;

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toast, setToast] = useState<Toast | null>(null);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const addToast = (message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info', duration: number = 5000) => {
        // Очищаем предыдущий таймер
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        // Используем функциональное обновление для получения актуального состояния
        setToast((prevToast) => {
            // Если есть текущий toast, запускаем анимацию исчезновения
            if (prevToast) {
                // Помечаем текущий toast как исчезающий
                setTimeout(() => {
                    const id = Date.now();
                    setToast({ id, message, type, duration });
                }, EXIT_ANIMATION_DURATION);
                
                return { ...prevToast, isExiting: true };
            } else {
                // Если нет текущего toast, показываем сразу
                const id = Date.now();
                return { id, message, type, duration };
            }
        });
    };

    const removeToast = () => {
        setToast((prevToast) => {
            if (prevToast) {
                // Помечаем как исчезающий
                setTimeout(() => {
                    setToast(null);
                }, EXIT_ANIMATION_DURATION);
                
                return { ...prevToast, isExiting: true };
            }
            return null;
        });
    };

    useEffect(() => {
        if (toast && !toast.isExiting) {
            timeoutRef.current = setTimeout(() => {
                removeToast();
            }, toast.duration);
            
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [toast]);

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}
            <div className={styles.toastContainer}>
                {toast && (
                    <div 
                        key={toast.id} 
                        className={`${styles.toast} ${styles[toast.type]} ${toast.isExiting ? styles.exiting : ''}`}
                    >
                        {toast.message}
                    </div>
                )}
            </div>
        </ToastContext.Provider>
    );
};
